import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import DatePicker from "./DatePicker";
import CustomSelect from "./CustomSelect";
import ConfirmDialog from "./ConfirmDialog";
import { APPT_PERSON_TEMPLATE_PATTERN } from "../constants";
import "./AppointmentForm.css";

function getAbbreviation(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

// The STORED Kürzel wins; name initials are only the fallback. Template texts
// ("MB Arbeit") are matched all over the calendar with the shared constants
// patterns, so an admin-defined Kürzel must be what actually lands in the text.
function abbrevOf(employee, fallbackName) {
  return (employee?.abbreviation || getAbbreviation(employee?.name || fallbackName)).toUpperCase();
}

const TEMPLATE_TYPES = [
  { value: "Arbeit", label: "Arbeit" },
  { value: "Arbeit VM", label: "Arbeit Vormittag" },
  { value: "Arbeit NM", label: "Arbeit Nachmittag" },
  { value: "Office", label: "Office" },
  { value: "Office VM", label: "Office Vormittag" },
  { value: "Office NM", label: "Office Nachmittag" },
];

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 8; h <= 17; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === 17 && m > 0) break;
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
})();

/* ── Hook: position a fixed dropdown relative to a trigger ── */
function useDropdownPos(triggerRef, open, align = "below") {
  const [pos, setPos] = useState(null);

  const update = useCallback(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    if (align === "above") {
      setPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width });
    } else {
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [open, triggerRef, align]);

  useEffect(() => { update(); }, [update]);

  return pos;
}

/* ── Time Input with Dropdown ── */
function TimeInput({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const pos = useDropdownPos(triggerRef, open, "above");

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fixed-position portal — page scrolling would detach it from the input;
  // close instead (scrolling inside the option list stays allowed).
  useEffect(() => {
    if (!open) return;
    const onScroll = (e) => {
      if (dropdownRef.current && e.target instanceof Node && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return (
    <div className="time-input-wrap">
      <input
        ref={triggerRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || "HH:MM"}
        className="time-input"
      />
      {open && pos && createPortal(
        <div
          ref={dropdownRef}
          className="time-dropdown"
          style={{ position: "fixed", bottom: pos.bottom, left: pos.left, width: pos.width }}
        >
          {TIME_OPTIONS.map((t) => (
            <button
              key={t}
              type="button"
              className={`time-option ${t === value ? "active" : ""}`}
              onClick={() => { onChange(t); setOpen(false); }}
            >
              {t}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ── Main Component ── */
export default function AppointmentForm({ date, employees, currentUser, isAdmin, existingAppointments, editingAppointment, onSubmit, onCancel, onDelete, onDirtyChange }) {
  const templatePattern = APPT_PERSON_TEMPLATE_PATTERN;
  const isTemplateEdit = editingAppointment && templatePattern.test(editingAppointment.text);
  const [activeTab, setActiveTab] = useState(editingAppointment ? (isTemplateEdit ? "template" : "manual") : "template");
  // Only a new appointment can actually toggle between the two tabs. In that case
  // both panels stay mounted and stacked (see .appointment-form-body--stacked), so
  // the dialog keeps the height of the taller one instead of resizing on switch.
  const stackedTabs = !editingAppointment;

  // Full list (incl. archived) — needed so an existing assignment still resolves
  // to a name. Pickers below use the filtered lists instead.
  const employeeList = useMemo(() => employees || [], [employees]);

  const currentUserName = (() => {
    if (!currentUser) return "";
    const match = employeeList.find(
      (m) => (m.name || m).toLowerCase() === currentUser.username?.toLowerCase()
    );
    return match ? (match.name || match) : currentUser.username || "";
  })();

  // Template state — pre-fill from editing appointment if it's a template
  const editTemplateMatch = isTemplateEdit ? editingAppointment.text.match(templatePattern) : null;
  // Resolve by the STORED Kürzel first, then by name initials (old templates
  // were created from initials). Only when neither matches fall back to the
  // current user — matching both ways prevents an edit from silently
  // re-targeting the template to whoever happens to be editing it.
  const editTemplatePerson = editTemplateMatch
    ? (employeeList.find((m) => abbrevOf(m) === editTemplateMatch[1])?.name
        || employeeList.find((m) => getAbbreviation(m.name || m) === editTemplateMatch[1])?.name
        || currentUserName)
    : currentUserName;
  const [templatePerson, setTemplatePerson] = useState(editTemplatePerson);
  const [templateType, setTemplateType] = useState(editTemplateMatch ? editTemplateMatch[2] : "Arbeit");
  const [templateError, setTemplateError] = useState("");

  // Manual state
  const [text, setText] = useState(editingAppointment?.text || "");
  const [appointmentDate, setAppointmentDate] = useState(editingAppointment?.date || date || "");
  const [startTime, setStartTime] = useState(editingAppointment?.start_time || "");
  const [endTime, setEndTime] = useState(editingAppointment?.end_time || "");
  const [personIds, setPersonIds] = useState(editingAppointment?.person_ids || []);
  const [manualError, setManualError] = useState("");

  // Only active staff is offered. Archived people already on this appointment
  // keep their chip (captured once, so de-selecting stays undoable) — the
  // assignment must not silently disappear just because they were archived.
  const initialPersonIds = useRef(editingAppointment?.person_ids || []);
  const personChips = useMemo(() => [
    ...employeeList.filter((m) => !m.archived),
    ...employeeList.filter((m) => m.archived && initialPersonIds.current.includes(m.id)),
  ], [employeeList]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Report whether any field differs from its initial value — the parent uses
  // this to guard the backdrop-close against silently discarding input.
  const dirtySnapshotRef = useRef(null);
  useEffect(() => {
    if (!onDirtyChange) return;
    const snap = JSON.stringify({ templatePerson, templateType, text, appointmentDate, startTime, endTime, personIds });
    if (dirtySnapshotRef.current === null) { dirtySnapshotRef.current = snap; return; }
    onDirtyChange(snap !== dirtySnapshotRef.current);
  }, [onDirtyChange, templatePerson, templateType, text, appointmentDate, startTime, endTime, personIds]);
  useEffect(() => () => { if (onDirtyChange) onDirtyChange(false); }, [onDirtyChange]);

  // The stacked inactive panel is visibility:hidden and therefore not focusable,
  // so autoFocus can't do the job — focus the name field whenever Manuell opens.
  const textRef = useRef(null);
  useEffect(() => {
    if (activeTab === "manual") textRef.current?.focus();
  }, [activeTab]);

  const dayAppointments = (existingAppointments || []).filter((t) => t.date === date);

  // Resolve the picked NAME back to the employee so the stored Kürzel is used.
  const templateEmployee = employeeList.find((m) => (m.name || m) === templatePerson);

  const isTemplateDuplicate = () => {
    const abbreviation = abbrevOf(templateEmployee, templatePerson);
    const appointmentText = `${abbreviation} ${templateType}`;
    return dayAppointments.some((t) => t.text === appointmentText && (!editingAppointment || t.id !== editingAppointment.id));
  };

  const handleTemplateSubmit = (e) => {
    e.preventDefault();
    setTemplateError("");
    if (!templatePerson) {
      setTemplateError("Bitte Person auswählen.");
      return;
    }
    const abbreviation = abbrevOf(templateEmployee, templatePerson);
    const appointmentText = `${abbreviation} ${templateType}`;
    if (isTemplateDuplicate()) {
      setTemplateError("Dieser Termin existiert bereits für diesen Tag.");
      return;
    }
    onSubmit({ date: editingAppointment?.date || date, text: appointmentText, start_time: null });
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    setManualError("");
    if (!text.trim() || !appointmentDate) {
      setManualError("Bitte Name und Datum ausfüllen.");
      return;
    }
    onSubmit({
      date: appointmentDate,
      text: text.trim(),
      start_time: startTime || null,
      end_time: endTime || null,
      person_ids: personIds,
    });
  };

  // Archived people are not selectable. An already saved template of an archived
  // person still shows their name — CustomSelect falls back to the raw value
  // when it is not among the options.
  const personOptions = isAdmin
    ? employeeList.filter((m) => !m.archived).map((m) => ({ value: m.name || m, label: m.name || m }))
    : employeeList
        .filter((m) => !m.archived && (m.name || m).toLowerCase() === currentUser?.username?.toLowerCase())
        .map((m) => ({ value: m.name || m, label: m.name || m }));

  return (
    <div className="appointment-form-wrap">
      <div className="modal-header">
        <h2 className="modal-title">{editingAppointment ? (isTemplateEdit ? "Tag bearbeiten" : "Termin bearbeiten") : "Neuer Termin"}</h2>
        <button type="button" className="modal-close-btn" onClick={onCancel} title="Schließen">&times;</button>
      </div>

      {(!editingAppointment || isTemplateEdit) && (
        <div className="appointment-form-tabs" style={isTemplateEdit ? { display: "none" } : {}}>

          <button
            type="button"
            className={`appointment-form-tab ${activeTab === "template" ? "active" : ""}`}
            onClick={() => setActiveTab("template")}
          >
            Vorlage
          </button>
          <button
            type="button"
            className={`appointment-form-tab ${activeTab === "manual" ? "active" : ""}`}
            onClick={() => setActiveTab("manual")}
          >
            Manuell
          </button>
        </div>
      )}

      <div className={`appointment-form-body${stackedTabs ? " appointment-form-body--stacked" : ""}`}>
        {(stackedTabs || activeTab === "template") && (
          <form
            className={`appointment-form${activeTab === "template" ? "" : " is-inactive"}`}
            onSubmit={handleTemplateSubmit}
          >
            {templateError && <div className="form-error">{templateError}</div>}

            <div className="form-row-half">
              <div className="form-row">
                <label>Person</label>
                <CustomSelect
                  value={templatePerson}
                  options={personOptions}
                  onChange={setTemplatePerson}
                  disabled={!isAdmin}
                />
              </div>
              <div className="form-row">
                <label>Typ</label>
                <CustomSelect
                  value={templateType}
                  options={TEMPLATE_TYPES}
                  onChange={setTemplateType}
                />
              </div>
            </div>

            {isTemplateDuplicate() && (
              <div className="form-duplicate-hint">Existiert bereits für diesen Tag</div>
            )}

            <div className="form-actions">
              {isTemplateEdit && onDelete && (
                <button type="button" className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>Löschen</button>
              )}
              <div className="form-actions-right">
                <button type="submit" className="btn btn-primary" disabled={isTemplateDuplicate()}>{isTemplateEdit ? "Speichern" : "Erstellen"}</button>
              </div>
            </div>
          </form>
        )}

        {(stackedTabs || activeTab === "manual") && (
          <form
            className={`appointment-form${activeTab === "manual" ? "" : " is-inactive"}`}
            onSubmit={handleManualSubmit}
          >
            {manualError && <div className="form-error">{manualError}</div>}

            <div className="form-row-half">
              <div className="form-row">
                <label>Datum</label>
                <DatePicker value={appointmentDate} onChange={setAppointmentDate} />
              </div>
              <div className="form-row">
                <label>Name</label>
                <input
                  ref={textRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Termin-Bezeichnung"
                />
              </div>
            </div>

            <div className="form-row-half">
              <div className="form-row">
                <label>Von</label>
                <TimeInput value={startTime} onChange={setStartTime} placeholder="09:00" />
              </div>
              <div className="form-row">
                <label>Bis</label>
                <TimeInput value={endTime} onChange={setEndTime} placeholder="17:00" />
              </div>
            </div>

            <div className="form-row">
              <label>Personen</label>
              <div className="task-person-chips">
                {personChips.map((m) => {
                  const selected = personIds.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      className={`task-person-chip ${selected ? "task-person-chip-active" : ""}`}
                      style={selected ? { background: m.color, borderColor: m.color } : {}}
                      title={m.archived ? `${m.name} (archiviert)` : m.name}
                      onClick={() => setPersonIds(selected ? personIds.filter((pid) => pid !== m.id) : [...personIds, m.id])}
                    >
                      {m.abbreviation || m.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-actions">
              {onDelete && editingAppointment && (
                <button type="button" className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>Löschen</button>
              )}
              <div className="form-actions-right">
                <button type="submit" className="btn btn-primary">{editingAppointment ? "Speichern" : "Erstellen"}</button>
              </div>
            </div>
          </form>
        )}
      </div>
      {showDeleteConfirm && (
        <ConfirmDialog
          message={`Termin "${editingAppointment?.text}" wirklich löschen?`}
          onConfirm={onDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}
